import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import useIsPhone from "features/files/use-is-phone";
import { deskAttachmentUrl } from "services/desk.service";
import { cidKey } from "./desk-email-cids";
import { DeskAttachmentItem } from "types/Desk";

const PURIFY_CONFIG = {
  ADD_ATTR: [
    "target",
    "style",
    "align",
    "valign",
    "bgcolor",
    "background",
    "width",
    "height",
    "border",
    "cellpadding",
    "cellspacing",
    "colspan",
    "rowspan",
    "role",
  ],
  ADD_TAGS: ["center", "font"],
  ALLOW_DATA_ATTR: false,
};

DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node instanceof Element && node.hasAttribute("target")) {
    node.setAttribute("rel", "noopener noreferrer");
  }
});

/**
 * Containers every mail client uses to wrap the previous message. A bare
 * <blockquote> is deliberately NOT here: newsletters and notification mails use
 * it for pull quotes, and treating the first one as the start of the thread
 * hides the rest of a perfectly normal message.
 */
const QUOTE_SELECTOR = [
  "blockquote[type='cite']",
  "blockquote.gmail_quote",
  ".gmail_quote",
  ".yahoo_quoted",
  ".moz-cite-prefix",
  "#divRplyFwdMsg",
  "#appendonsend",
  "div[id^='mail-editor-reference-message-container']",
].join(",");

const SEPARATOR_RE = /^\s*[-_]{2,}\s*(original message|forwarded message|pradinis laiškas|persiųsta žinutė)\s*[-_]{2,}/i;
const HEADER_RE = /^\s*(from|nuo|siuntėjas)\s*:/i;
const HEADER_FOLLOWUP_RE = /\s(sent|date|data|išsiųsta|to|kam|subject|tema)\s*:/i;
const WROTE_RE = /\b(wrote|rašė)\s*:$/i;

function rewriteCidSources(
  html: string,
  attachments: DeskAttachmentItem[],
  companyId: string
): string {
  if (!attachments.length) return html;
  const byCid = new Map<string, string>();
  for (const att of attachments) {
    if (!att.contentId || !att.isInline) continue;
    const key = cidKey(att.contentId);
    if (key) byCid.set(key, deskAttachmentUrl(companyId, att.id));
  }
  if (!byCid.size) return html;
  return html.replace(
    /(\s(?:src|background)\s*=\s*)(["'])cid:([^"']+)\2/gi,
    (full, prefix, quote, cid) => {
      const url = byCid.get(cidKey(cid));
      return url ? `${prefix}${quote}${url}${quote}` : full;
    }
  );
}

/** True for the node that starts the quoted part of a reply chain. */
function isQuoteStart(node: Node): boolean {
  if (node.nodeType === Node.ELEMENT_NODE) {
    const el = node as Element;
    if (el.matches(QUOTE_SELECTOR)) return true;
  }
  const text = (node.textContent || "").replace(/ /g, " ").trim();
  if (!text) return false;
  if (SEPARATOR_RE.test(text)) return true;
  // "On 12 Aug 2026, Jonas wrote:" — one short line, never a whole body.
  if (text.length < 300 && WROTE_RE.test(text)) return true;
  // Outlook pastes the previous headers as plain text instead of a container.
  return HEADER_RE.test(text) && HEADER_FOLLOWUP_RE.test(text.slice(0, 400));
}

function findQuoteStart(doc: Document): Node | null {
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    if (isQuoteStart(node)) return node;
    node = walker.nextNode();
  }
  return null;
}

/** Drops the quote and everything after it, keeping the reply written above. */
function collapseFrom(node: Node, root: Element): void {
  let cur: Node = node;
  while (cur.parentNode && cur !== root) {
    let sib = cur.nextSibling;
    while (sib) {
      const next = sib.nextSibling;
      sib.parentNode?.removeChild(sib);
      sib = next;
    }
    cur = cur.parentNode;
  }
  node.parentNode?.removeChild(node);
}

function textLength(body: HTMLElement): number {
  return (body.textContent || "").replace(/ /g, " ").trim().length;
}

function isVisuallyEmpty(body: HTMLElement): boolean {
  if (body.querySelector("img,table,hr")) return false;
  return (body.textContent || "").replace(/ /g, " ").trim().length < 5;
}

/**
 * A declaration boundary, so `line-height:0` is never read as `height:0`.
 * `display:block;line-height:0` sits on the hero image of every Mailchimp
 * template — matching it would silently delete the picture.
 */
function tinyDimensionRe(prop: "width" | "height"): RegExp {
  return new RegExp(`(?:^|;)\\s*(?:max-|min-)?${prop}\\s*:\\s*[0-3](?:\\.\\d+)?(?:px)?\\s*(?:;|$)`);
}

/** Tracking pixels carry no meaning — a placeholder for them is just noise. */
function isTrackingPixel(el: Element): boolean {
  const attr = (name: string) => {
    const raw = el.getAttribute(name);
    if (raw == null) return null;
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? null : n;
  };
  const style = (el.getAttribute("style") || "").toLowerCase();
  // Both dimensions, not either — and the same rule whether they arrive as
  // attributes or as CSS. A 1px-tall spacer with a real width is a layout
  // rule, and a wide banner may legitimately declare height="1".
  const w = attr("width");
  const h = attr("height");
  const tinyWidth = (w !== null && w <= 3) || tinyDimensionRe("width").test(style);
  const tinyHeight = (h !== null && h <= 3) || tinyDimensionRe("height").test(style);
  return tinyWidth && tinyHeight;
}

/**
 * Remote images are blocked until the reader asks for them (they leak the read
 * to the sender). Inline cid: parts and data: URIs are ours and stay.
 */
function blockRemoteImages(
  doc: Document,
  isAllowed: (url: string) => boolean,
  placeholder: string
): number {
  let blocked = 0;
  doc.querySelectorAll("img").forEach((img) => {
    const src = img.getAttribute("src") || "";
    if (!src || isAllowed(src)) return;
    if (isTrackingPixel(img)) {
      img.remove();
      return;
    }
    blocked += 1;
    const chip = doc.createElement("span");
    chip.className = "desk-blocked-img";
    chip.setAttribute("data-blocked-src", src);
    chip.textContent = `🖼 ${img.getAttribute("alt") || placeholder}`;
    img.replaceWith(chip);
  });
  // srcset wins over src in the browser and the CSP would block it anyway —
  // without stripping it the image just disappears and nothing offers to load it.
  doc.querySelectorAll("img[srcset], source[srcset]").forEach((el) => {
    const srcset = el.getAttribute("srcset") || "";
    if (!srcset) return;
    const urls = srcset.split(",").map((part) => part.trim().split(/\s+/)[0]);
    if (urls.every((url) => !url || isAllowed(url))) return;
    blocked += 1;
    el.setAttribute("data-blocked-srcset", srcset);
    el.removeAttribute("srcset");
  });
  doc.querySelectorAll("[background]").forEach((el) => {
    const url = el.getAttribute("background") || "";
    if (!url || isAllowed(url)) return;
    blocked += 1;
    el.setAttribute("data-blocked-background", url);
    el.removeAttribute("background");
  });
  doc.querySelectorAll("[style*='url']").forEach((el) => {
    const style = el.getAttribute("style") || "";
    const stripped = style.replace(
      /url\(\s*(['"]?)([^)'"]+)\1\s*\)/gi,
      (full: string, _quote: string, url: string) => (isAllowed(url) ? full : "none")
    );
    if (stripped === style) return;
    blocked += 1;
    el.setAttribute("style", stripped);
  });
  // DOMPurify keeps <style>, and a stylesheet can fetch just as loudly as an
  // <img> — background:url(), @import and @font-face all phone home on open.
  doc.querySelectorAll("style").forEach((el) => {
    const css = el.textContent || "";
    if (!/url\(|@import/i.test(css)) return;
    const stripped = css
      .replace(/@import\s+[^;]+;/gi, "")
      .replace(
        /url\(\s*(['"]?)([^)'"]+)\1\s*\)/gi,
        (full: string, _quote: string, url: string) => (isAllowed(url) ? full : "none")
      );
    if (stripped === css) return;
    blocked += 1;
    el.textContent = stripped;
  });
  return blocked;
}

function buildSrcDoc(bodyHtml: string, showImages: boolean, imgSources: string): string {
  const imgPolicy = showImages ? `${imgSources} https: http:` : imgSources;
  // default-src 'none' is the point: img-src alone left @font-face and any
  // surviving fetch type free to announce the read to the sender.
  const csp = `default-src 'none'; img-src ${imgPolicy}; style-src 'unsafe-inline'; font-src data:`;
  return `<!DOCTYPE html><html><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width, initial-scale=1"/><meta http-equiv="Content-Security-Policy" content="${csp}"/><base target="_blank" rel="noopener noreferrer"/><style>
html,body{margin:0;padding:0;background:#fff;}
body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;font-size:14px;line-height:1.5;color:#18181b;word-break:break-word;overflow-wrap:anywhere;}
img,video{max-width:100%!important;height:auto!important;}
table{border-collapse:collapse;}
a{color:#4f46e5;}
/* A blocked image is a chip on one line, never a 200px hole in the layout */
.desk-blocked-img{display:inline-block;max-width:100%;padding:1px 6px;border:1px dashed #d4d4d8;border-radius:6px;font-size:11px;line-height:1.6;color:#71717a;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;vertical-align:middle;}
/* Keep fixed-width marketing layouts readable via horizontal scroll instead of clipping */
.email-root{overflow-x:auto;max-width:100%;}
.email-root>*{max-width:100%;}
</style></head><body><div class="email-root">${bodyHtml}</div></body></html>`;
}

/**
 * Smallest scale a mail is shrunk to on a phone. A 1100 px newsletter fitted to
 * a 330 px screen came out at 0.29 — text too small to read — so below this
 * the mail stays readable and scrolls sideways inside its own box instead.
 */
export const EMAIL_MIN_ZOOM = 0.6;

/**
 * How far to shrink a fixed-width mail (a newsletter table) so it fits the
 * iframe, but never below EMAIL_MIN_ZOOM; null when it already fits. A mail
 * still wider than the frame at that scale scrolls inside `.email-root`.
 */
export function emailFitZoom(availableWidth: number, contentWidth: number): number | null {
  if (!availableWidth || contentWidth <= availableWidth) return null;
  return Math.max(EMAIL_MIN_ZOOM, availableWidth / contentWidth);
}

interface Props {
  html: string;
  companyId: string;
  attachments?: DeskAttachmentItem[];
  title?: string;
  className?: string;
}

/**
 * Gmail-style isolated HTML email renderer: sanitize → rewrite cid: → block
 * remote images → collapse the quoted thread → iframe srcdoc.
 */
export default function DeskEmailHtml({
  html,
  companyId,
  attachments = [],
  title = "email",
  className = "",
}: Props) {
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [showImages, setShowImages] = useState(false);
  const [showQuoted, setShowQuoted] = useState(false);

  const { srcDoc, blockedImages, hasQuote } = useMemo(() => {
    const attachmentBase = deskAttachmentUrl(companyId, "");
    const isAllowed = (url: string) =>
      url.startsWith("data:") || url.startsWith(attachmentBase);
    const withCids = rewriteCidSources(html, attachments, companyId);
    const clean = DOMPurify.sanitize(withCids, { ...PURIFY_CONFIG });
    const parse = () => new DOMParser().parseFromString(clean, "text/html");

    // A message that is nothing but a quote has nothing left to show once
    // collapsed, so it gets no toggle. And a "quote" that swallows most of the
    // body is a false positive (a pull quote, a notification that opens with
    // "From: …") — showing the whole message beats hiding the real content.
    let quoteToggle = false;
    const probe = parse();
    const probeStart = findQuoteStart(probe);
    if (probeStart) {
      const before = textLength(probe.body);
      collapseFrom(probeStart, probe.body);
      const after = textLength(probe.body);
      quoteToggle = !isVisuallyEmpty(probe.body) && (before === 0 || after * 2 >= before);
    }

    let doc = probe;
    if (!quoteToggle || showQuoted) {
      doc = parse();
    }
    const blocked = showImages ? 0 : blockRemoteImages(doc, isAllowed, t("desk_blocked_image"));

    let imgSources = "'self' data:";
    try {
      imgSources = `'self' data: ${new URL(attachmentBase, window.location.href).origin}`;
    } catch {
      /* relative API_URL — 'self' already covers it */
    }
    return {
      srcDoc: buildSrcDoc(doc.body.innerHTML, showImages, imgSources),
      blockedImages: blocked,
      hasQuote: quoteToggle,
    };
  }, [html, attachments, companyId, showImages, showQuoted, t]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const resize = () => {
      try {
        const doc = iframe.contentDocument;
        if (!doc) return;
        const root = doc.querySelector<HTMLElement>(".email-root");
        if (root) {
          root.style.removeProperty("zoom");
          const zoom = isPhone ? emailFitZoom(root.clientWidth, root.scrollWidth) : null;
          if (zoom) root.style.setProperty("zoom", String(zoom));
        }
        // documentElement.scrollHeight is floored at the iframe's own viewport,
        // so measuring without collapsing first would keep the tall height of
        // the previous render — an expanded quote that is then hidden would
        // leave screens of blank white behind.
        iframe.style.height = "0px";
        const height = Math.max(
          doc.body?.scrollHeight || 0,
          doc.documentElement?.scrollHeight || 0,
          80
        );
        iframe.style.height = `${height}px`;
      } catch {
        /* cross-origin — ignore */
      }
    };

    iframe.addEventListener("load", resize);
    // Marketing emails load images async — remeasure a few times.
    const timers = [100, 400, 1200].map((ms) => window.setTimeout(resize, ms));
    // A turned phone or a resized panel changes the width the mail wraps at.
    // Only width counts: resize() itself changes the iframe's height.
    let lastWidth = iframe.clientWidth;
    const observer =
      typeof ResizeObserver === "function"
        ? new ResizeObserver(() => {
            if (iframe.clientWidth === lastWidth) return;
            lastWidth = iframe.clientWidth;
            resize();
          })
        : null;
    observer?.observe(iframe);
    return () => {
      iframe.removeEventListener("load", resize);
      timers.forEach(clearTimeout);
      observer?.disconnect();
    };
  }, [srcDoc, isPhone]);

  return (
    <div className={className}>
      {blockedImages > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-500/20 bg-amber-500/10 px-3 py-1.5 text-[11px] text-amber-800 dark:text-amber-300">
          <span>{t("desk_images_blocked", { count: blockedImages })}</span>
          <button
            type="button"
            onClick={() => setShowImages(true)}
            className="rounded-lg bg-amber-500/20 px-2.5 py-0.5 font-semibold hover:bg-amber-500/30 transition-colors max-md:min-h-11 max-md:px-4"
          >
            {t("desk_show_images")}
          </button>
        </div>
      )}
      <iframe
        ref={iframeRef}
        title={title}
        srcDoc={srcDoc}
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        referrerPolicy="no-referrer"
        className="block w-full border-0 bg-white rounded-lg"
        style={{ minHeight: 80 }}
      />
      {hasQuote && (
        <div className="px-2 pb-2 bg-white">
          <button
            type="button"
            onClick={() => setShowQuoted((prev) => !prev)}
            aria-expanded={showQuoted}
            title={t(showQuoted ? "desk_hide_quoted" : "desk_show_quoted")}
            aria-label={t(showQuoted ? "desk_hide_quoted" : "desk_show_quoted")}
            className="rounded-md bg-zinc-100 px-2 py-0.5 text-xs font-bold leading-none text-zinc-500 hover:bg-zinc-200 hover:text-zinc-700 transition-colors max-md:min-h-11 max-md:px-4"
          >
            •••
          </button>
        </div>
      )}
    </div>
  );
}
